/**
 * Temp_excelSheetModel — 엑셀 시트를 화면에 그릴 값으로 바꾼다 (Temp_ExcelPreview 가 쓴다, D-07 §3.5)
 *
 * React·DOM 에 기대지 않는 순수 함수다. 파일에 적힌 병합·열 너비·행 높이·채우기·테두리·글꼴·정렬·표시형식을 읽어
 * 인쇄 미리보기처럼 보이도록 px 값으로 정리한다. 색·크기는 파일이 가진 "데이터"라 디자인 토큰이 아니다.
 *
 * 한계(의도적으로 단순화): 서식 조건·차트·도형·그라데이션 채우기·글자 일부 서식(rich text)·글자 회전은 그리지 않는다.
 */
import type { Cell, CellValue, Row, Workbook, Worksheet } from 'exceljs'

/** 인쇄 용지는 화면 테마와 상관없이 흰 종이에 검은 글자다(다크 모드에서도 같은 모양이어야 한다) */
export const SHEET_PAPER = '#ffffff' // dq-ignore
const SHEET_INK = '#000000' // dq-ignore

/** 큰 시트는 앞부분만 읽는다 — 화면이 느려지지 않도록 */
export const MAX_SHEET_ROWS = 500
export const MAX_SHEET_COLS = 50

export interface SheetEdge { width: number; style: 'solid' | 'dashed' | 'dotted' | 'double'; color: string }

export interface SheetBox {
  key: string
  row: number
  col: number
  rowSpan: number
  colSpan: number
  text: string
  fill?: string
  color: string
  font?: string
  fontSize: number
  bold: boolean
  italic: boolean
  underline: boolean
  strike: boolean
  align: 'left' | 'center' | 'right'
  valign: 'flex-start' | 'center' | 'flex-end'
  wrap: boolean
  indent: number
  spill: boolean
  top?: SheetEdge
  left?: SheetEdge
  right?: SheetEdge
  bottom?: SheetEdge
}

export interface SheetImage {
  data: Uint8Array
  mime: string
  left: number
  top: number
  width: number
  height: number
}

export interface SheetModel {
  name: string
  colWidths: number[]
  rowHeights: number[]
  boxes: SheetBox[]
  images: SheetImage[]
  width: number
  height: number
  /** 행·열 상한 때문에 뒤쪽을 잘랐는지 */
  clipped: boolean
}

// ── 색 ────────────────────────────────────────────────────────────────────────

interface RawColor { argb?: string; theme?: number; tint?: number; indexed?: number }

// 엑셀 기본 테마(Office) — 파일 안의 테마 정의는 읽지 않는다
const THEME = ['FFFFFF', '000000', 'E7E6E6', '44546A', '4472C4', 'ED7D31', 'A5A5A5', 'FFC000', '5B9BD5', '70AD47']
const INDEXED: Record<number, string> = {
  0: '000000', 1: 'FFFFFF', 2: 'FF0000', 3: '00FF00', 4: '0000FF', 5: 'FFFF00', 6: 'FF00FF', 7: '00FFFF',
  8: '000000', 9: 'FFFFFF', 10: 'FF0000', 11: '00FF00', 12: '0000FF', 13: 'FFFF00', 14: 'FF00FF', 15: '00FFFF',
}

function applyTint(hex: string, tint: number): string {
  const ch = [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16))
  const out = ch.map(v => Math.round(tint > 0 ? v + (255 - v) * tint : v * (1 + tint)))
  return out.map(v => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('')
}

function resolveColor(c: RawColor | undefined): string | undefined {
  if (!c) return undefined
  let hex: string | undefined
  if (c.argb) hex = c.argb.length === 8 ? c.argb.slice(2) : c.argb
  else if (c.theme != null) hex = THEME[c.theme]
  else if (c.indexed != null) hex = INDEXED[c.indexed]
  if (!hex || !/^[0-9a-fA-F]{6}$/.test(hex)) return undefined
  if (c.tint) hex = applyTint(hex, c.tint)
  return `#${hex}`
}

// ── 값 → 글자 ─────────────────────────────────────────────────────────────────

type Kind = 'text' | 'number' | 'bool' | 'error'

const general = (n: number): string => (Number.isInteger(n) ? String(n) : String(Number(n.toPrecision(10))))

const group = (intPart: string): string => intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

function splitSections(fmt: string): string[] {
  const out: string[] = []
  let cur = ''
  let quoted = false
  for (let i = 0; i < fmt.length; i++) {
    const ch = fmt[i]
    if (ch === '"') quoted = !quoted
    if (ch === ';' && !quoted) { out.push(cur); cur = '' } else cur += ch
  }
  out.push(cur)
  return out
}

/** 숫자 표시형식 — 자주 쓰는 모양(#,##0 · 0.00 · 0% · 통화 기호 · 문자 덧붙임 · 음수 괄호)만 다룬다 */
export function formatNumber(value: number, fmt?: string): string {
  if (!fmt || fmt === 'General' || fmt === '@') return general(value)
  const sections = splitSections(fmt)
  let sec = sections[0]
  let n = value
  if (sections.length >= 3 && n === 0) sec = sections[2]
  else if (sections.length >= 2 && n < 0) { sec = sections[1]; n = Math.abs(n) }

  let before = ''
  let after = ''
  let run = ''
  let percent = false
  let seenRun = false
  const put = (s: string) => { if (seenRun) after += s; else before += s }

  for (let i = 0; i < sec.length; i++) {
    const ch = sec[i]
    if (ch === '"') {
      const end = sec.indexOf('"', i + 1)
      const lit = sec.slice(i + 1, end < 0 ? sec.length : end)
      put(lit)
      i = end < 0 ? sec.length : end
    } else if (ch === '\\') { put(sec[i + 1] ?? ''); i++ }
    else if (ch === '_' || ch === '*') { i++ } // 자리 맞춤·채움 문자는 화면에서 의미가 없다
    else if (ch === '[') {
      const end = sec.indexOf(']', i)
      const inner = sec.slice(i + 1, end < 0 ? sec.length : end)
      if (inner.startsWith('$')) put(inner.slice(1).split('-')[0]) // [$₩-412] → ₩
      i = end < 0 ? sec.length : end
    } else if ('0#?.,'.includes(ch) && !(seenRun && after !== '')) {
      run += ch
      seenRun = true
    } else if (ch === '%') { percent = true; put('%') }
    else if (/[eE]/.test(ch) && seenRun && /[+-]/.test(sec[i + 1] ?? '')) return general(value) // 지수 표기는 다루지 않는다
    else put(ch)
  }

  const negPrefix = sections.length < 2 && value < 0 ? '-' : ''
  if (!seenRun) return `${negPrefix}${before}${after}`

  // 끝의 쉼표는 천 단위 축소(1,000 으로 나눔)
  let scale = 0
  while (run.endsWith(',')) { run = run.slice(0, -1); scale++ }
  let v = Math.abs(sections.length < 2 ? value : n)
  if (percent) v *= 100
  v /= 1000 ** scale

  const dot = run.indexOf('.')
  const intRun = (dot < 0 ? run : run.slice(0, dot)).replace(/,/g, '')
  const hasGroup = run.includes(',')
  const decRun = dot < 0 ? '' : run.slice(dot + 1).replace(/,/g, '')
  const minDec = (decRun.match(/0/g) ?? []).length // 0 은 항상 자리를 채우고 #·? 는 뒤쪽 0 을 줄인다
  const minInt = (intRun.match(/0/g) ?? []).length

  const fixed = v.toFixed(decRun.length)
  let [ip, fp = ''] = fixed.split('.')
  while (fp.length > minDec && fp.endsWith('0')) fp = fp.slice(0, -1)
  if (ip === '0' && minInt === 0) ip = ''
  if (ip.length < minInt) ip = ip.padStart(minInt, '0')
  const body = (hasGroup ? group(ip) : ip) + (decRun.length > 0 ? `.${fp}` : '')
  return `${negPrefix}${before}${body}${after}`
}

const WEEK_KO = ['일', '월', '화', '수', '목', '금', '토']
const WEEK_EN = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTH_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/** 날짜 표시형식 — yyyy-mm-dd 계열·시각·요일(aaa)·AM/PM 정도를 다룬다. 엑셀이 주는 Date 는 UTC 기준이다 */
export function formatDate(d: Date, fmt?: string): string {
  const f = fmt && /[ymdhs]/i.test(fmt.replace(/"[^"]*"/g, '')) ? fmt : 'yyyy-mm-dd'
  const tokens: { t: string; lit: boolean }[] = []
  const re = /"([^"]*)"|\[(?:h+|m+|s+)\]|AM\/PM|am\/pm|A\/P|yyyy|yy|mmmm|mmm|mm|m|dddd|ddd|dd|d|hh|h|ss|s|aaaa|aaa|\\(.)|[^a-zA-Z"\\]+|[a-zA-Z]/g
  let m: RegExpExecArray | null
  while ((m = re.exec(f))) {
    if (m[1] !== undefined) tokens.push({ t: m[1], lit: true })
    else if (m[2] !== undefined) tokens.push({ t: m[2], lit: true })
    else tokens.push({ t: m[0].replace(/^\[|\]$/g, ''), lit: !/^(AM\/PM|am\/pm|A\/P|y+|m+|d+|h+|s+|a+)$/i.test(m[0].replace(/^\[|\]$/g, '')) })
  }
  const has12 = tokens.some(x => !x.lit && /^(am\/pm|a\/p)$/i.test(x.t))
  const p2 = (n: number) => String(n).padStart(2, '0')
  const Y = d.getUTCFullYear(), Mo = d.getUTCMonth(), D = d.getUTCDate(), H = d.getUTCHours(), Mi = d.getUTCMinutes(), S = d.getUTCSeconds(), W = d.getUTCDay()
  const H12 = H % 12 === 0 ? 12 : H % 12
  return tokens.map((x, i) => {
    if (x.lit) return x.t
    const t = x.t
    const lower = t.toLowerCase()
    if (/^m+$/.test(lower) && t.length <= 2) {
      // m 은 바로 앞 시(h)나 바로 뒤 초(s)와 이어지면 "분", 아니면 "월"
      let prev = i - 1
      while (prev >= 0 && tokens[prev].lit) prev--
      let next = i + 1
      while (next < tokens.length && tokens[next].lit) next++
      const isMinute = (prev >= 0 && /^h+$/i.test(tokens[prev].t) && i - prev <= 2) || (next < tokens.length && /^s+$/i.test(tokens[next].t) && next - i <= 2)
      if (isMinute) return t.length === 2 ? p2(Mi) : String(Mi)
      return t.length === 2 ? p2(Mo + 1) : String(Mo + 1)
    }
    switch (lower) {
      case 'yyyy': return String(Y)
      case 'yy': return p2(Y % 100)
      case 'mmmm': return MONTH_EN[Mo]
      case 'mmm': return MONTH_EN[Mo].slice(0, 3)
      case 'dd': return p2(D)
      case 'd': return String(D)
      case 'dddd': return WEEK_EN[W]
      case 'ddd': return WEEK_EN[W].slice(0, 3)
      case 'aaa': return WEEK_KO[W]
      case 'aaaa': return `${WEEK_KO[W]}요일`
      case 'hh': return p2(has12 ? H12 : H)
      case 'h': return String(has12 ? H12 : H)
      case 'ss': return p2(S)
      case 's': return String(S)
      case 'am/pm': return H < 12 ? 'AM' : 'PM'
      case 'a/p': return H < 12 ? 'A' : 'P'
      default: return t
    }
  }).join('')
}

function valueText(v: CellValue, fmt?: string): { text: string; kind: Kind } {
  if (v == null) return { text: '', kind: 'text' }
  if (typeof v === 'number') return { text: formatNumber(v, fmt), kind: 'number' }
  if (typeof v === 'string') return { text: v, kind: 'text' }
  if (typeof v === 'boolean') return { text: v ? 'TRUE' : 'FALSE', kind: 'bool' }
  if (v instanceof Date) return { text: formatDate(v, fmt), kind: 'number' }
  const o = v as unknown as Record<string, unknown>
  if (Array.isArray(o.richText)) return { text: (o.richText as { text: string }[]).map(t => t.text).join(''), kind: 'text' }
  if ('formula' in o || 'sharedFormula' in o) return valueText((o.result ?? null) as CellValue, fmt)
  if ('hyperlink' in o) return valueText((o.text ?? '') as CellValue, fmt)
  if ('error' in o) return { text: String(o.error), kind: 'error' }
  return { text: '', kind: 'text' }
}

// ── 테두리·정렬 ───────────────────────────────────────────────────────────────

const EDGE: Record<string, Omit<SheetEdge, 'color'>> = {
  thin: { width: 1, style: 'solid' },
  hair: { width: 1, style: 'dotted' },
  dotted: { width: 1, style: 'dotted' },
  dashed: { width: 1, style: 'dashed' },
  dashDot: { width: 1, style: 'dashed' },
  dashDotDot: { width: 1, style: 'dashed' },
  medium: { width: 2, style: 'solid' },
  mediumDashed: { width: 2, style: 'dashed' },
  mediumDashDot: { width: 2, style: 'dashed' },
  mediumDashDotDot: { width: 2, style: 'dashed' },
  slantDashDot: { width: 2, style: 'dashed' },
  thick: { width: 3, style: 'solid' },
  double: { width: 3, style: 'double' },
}

function edgeOf(b: { style?: string; color?: RawColor } | undefined): SheetEdge | undefined {
  if (!b || !b.style || !EDGE[b.style]) return undefined
  return { ...EDGE[b.style], color: resolveColor(b.color) ?? SHEET_INK }
}

// ── 범위 ──────────────────────────────────────────────────────────────────────

const colNo = (letters: string): number => letters.toUpperCase().split('').reduce((a, ch) => a * 26 + ch.charCodeAt(0) - 64, 0)

interface Range { r1: number; c1: number; r2: number; c2: number }

function parseRange(s: string): Range | null {
  const m = /^\$?([A-Za-z]+)\$?(\d+)(?::\$?([A-Za-z]+)\$?(\d+))?$/.exec(s.trim())
  if (!m) return null
  const c1 = colNo(m[1]), r1 = Number(m[2])
  return { r1, c1, r2: m[4] ? Number(m[4]) : r1, c2: m[3] ? colNo(m[3]) : c1 }
}

/** 인쇄 영역이 하나로 정해져 있으면 그 범위만 보여 준다(인쇄 미리보기와 같게) */
function printWindow(ws: Worksheet): Range | null {
  const raw = ws.pageSetup?.printArea
  if (!raw || typeof raw !== 'string') return null
  const first = raw.split(/&&|,/)[0].split('!').pop() ?? ''
  const r = parseRange(first)
  return r && r.r2 >= r.r1 && r.c2 >= r.c1 ? r : null
}

const approxTextWidth = (text: string, fontSize: number): number =>
  [...text].reduce((w, ch) => w + (/[\u1100-\u11ff\u3130-\u318f\uac00-\ud7af\u4e00-\u9fff\uff00-\uffef]/.test(ch) ? fontSize : fontSize * 0.56), 0)

/** 행에 실제로 있는 셀만(값 없이 서식만 있는 셀 포함). eachCell({includeEmpty}) 는 마지막 칸까지 빈 셀을 만들어 내서 쓰지 않는다 */
function cellsOf(row: Row): Cell[] {
  const out: Cell[] = []
  const models = (row.model as { cells?: { address: string }[] } | null)?.cells ?? []
  for (const m of models) {
    const c = row.findCell(colNo(/^[A-Za-z]+/.exec(m.address)?.[0] ?? 'A'))
    if (c) out.push(c)
  }
  return out
}

const colOf = (cell: Cell): number => colNo(/^[A-Za-z]+/.exec(cell.address)?.[0] ?? 'A')

/** 화면에 그릴 내용이 있는 칸인지 — 글자·색 채우기·테두리 중 하나. 흰색 채우기는 "칠하지 않음"과 같아 세지 않는다 */
function hasInk(cell: Cell): boolean {
  if (valueText(cell.value, cell.numFmt).text) return true
  const f = cell.fill as { type?: string; pattern?: string; fgColor?: RawColor } | undefined
  if (f?.type === 'pattern' && f.pattern === 'solid') {
    const c = resolveColor(f.fgColor)
    if (c && c.toLowerCase() !== SHEET_PAPER) return true
  }
  const b = cell.border as Record<string, { style?: string } | undefined> | undefined
  return !!b && Object.values(b).some(e => !!e?.style && !!EDGE[e.style])
}

/** 사용 범위를 읽는 최대 행 수 — 더 아래는 어차피 MAX_SHEET_ROWS 에서 잘린다 */
const SCAN_ROWS = 3000

// ── 시트 → 모델 ───────────────────────────────────────────────────────────────

export function buildSheetModel(ws: Worksheet, wb: Workbook): SheetModel {
  const win = printWindow(ws)
  const mergeList = (ws.model as { merges?: string[] }).merges ?? []

  // 사용 범위 — 글자·색·테두리가 있는 칸까지만 시트로 본다. 서식만 끝까지 칠해 둔 빈 열·행(<col max=16384> 같은 것)이
  // 시트를 끝없이 넓히면 내용이 왼쪽에 작게 몰려 보인다. 그림(도장·로고)과 내용 있는 병합 영역은 그 끝까지 넣는다.
  let usedRow = 0
  let usedCol = 0
  for (const im of ws.getImages()) {
    const tl = (im.range as { tl: { col: number; row: number } }).tl
    usedCol = Math.max(usedCol, Math.floor(tl.col) + 1)
    usedRow = Math.max(usedRow, Math.floor(tl.row) + 1)
  }
  for (let r = 1; r <= Math.min(ws.rowCount, SCAN_ROWS); r++) {
    const row = ws.findRow(r)
    if (!row) continue
    for (const cell of cellsOf(row)) {
      if (!hasInk(cell)) continue
      usedRow = Math.max(usedRow, r)
      usedCol = Math.max(usedCol, colOf(cell))
    }
  }
  for (const s of mergeList) {
    const m = parseRange(s)
    // 전체 행·열을 덮는 거대한 병합은 범위를 늘리지 않는다
    if (!m || m.c2 - m.c1 >= MAX_SHEET_COLS || m.r2 - m.r1 >= MAX_SHEET_ROWS) continue
    const master = ws.findRow(m.r1)?.findCell(m.c1)
    if (master && hasInk(master)) { usedRow = Math.max(usedRow, m.r2); usedCol = Math.max(usedCol, m.c2) }
  }

  const lastRow = win ? win.r2 : usedRow
  const lastCol = win ? win.c2 : usedCol
  const r0 = win ? win.r1 : 1
  const c0 = win ? win.c1 : 1
  const rEnd = Math.min(lastRow, r0 + MAX_SHEET_ROWS - 1)
  const cEnd = Math.min(lastCol, c0 + MAX_SHEET_COLS - 1)
  const clipped = rEnd < lastRow || cEnd < lastCol
  const nRows = Math.max(0, rEnd - r0 + 1)
  const nCols = Math.max(0, cEnd - c0 + 1)

  // 열 너비(문자 수 → px, 파일 값에 이미 여백 5px 이 들어 있다) · 행 높이(pt → px)
  const defCol = ws.properties?.defaultColWidth ?? 8.43
  const defRow = ws.properties?.defaultRowHeight ?? 15
  const colWidths: number[] = []
  for (let c = c0; c <= cEnd; c++) {
    const col = ws.getColumn(c)
    colWidths.push(col.hidden ? 0 : Math.round((col.width ?? defCol) * 7))
  }
  const rowHeights: number[] = []
  for (let r = r0; r <= rEnd; r++) {
    const row = ws.findRow(r)
    rowHeights.push(row?.hidden ? 0 : Math.round((row?.height ?? defRow) * 96 / 72))
  }

  // 병합 — 그리는 쪽은 왼쪽 위 셀 하나뿐이다
  const merges = new Map<string, Range>()
  const covered = new Set<string>()
  const key = (r: number, c: number) => `${r}:${c}`
  for (const s of mergeList) {
    const m = parseRange(s)
    if (!m || m.r2 < r0 || m.r1 > rEnd || m.c2 < c0 || m.c1 > cEnd || m.r1 < r0 || m.c1 < c0) continue
    const clamped = { ...m, r2: Math.min(m.r2, rEnd), c2: Math.min(m.c2, cEnd) }
    merges.set(key(m.r1, m.c1), clamped)
    for (let r = clamped.r1; r <= clamped.r2; r++) for (let c = clamped.c1; c <= clamped.c2; c++) if (r !== m.r1 || c !== m.c1) covered.add(key(r, c))
  }

  interface Draft extends SheetBox { rawTop?: SheetEdge; rawLeft?: SheetEdge; rawRight?: SheetEdge; rawBottom?: SheetEdge }
  const drafts: Draft[] = []
  const kinds: Kind[] = [] // drafts 와 같은 순서 — 값의 종류(글자 넘침 판단용)
  // 값 없이 테두리·채우기만 있는 칸(빈 양식 칸)도 그려야 하므로 cellsOf 로 서식만 있는 셀까지 읽는다
  for (let rn = r0; rn <= rEnd; rn++) {
    const row = ws.findRow(rn)
    if (!row || rowHeights[rn - r0] === 0) continue
    for (const cell of cellsOf(row)) {
      const cn = colOf(cell)
      if (cn < c0 || cn > cEnd || colWidths[cn - c0] === 0 || covered.has(key(rn, cn))) continue
      const merge = merges.get(key(rn, cn))
      const { text, kind } = valueText(cell.value, cell.numFmt)
      const fillRaw = cell.fill as { type?: string; pattern?: string; fgColor?: RawColor } | undefined
      const fill = fillRaw?.type === 'pattern' && fillRaw.pattern === 'solid' ? resolveColor(fillRaw.fgColor) : undefined
      const border = (cell.border ?? {}) as Record<string, { style?: string; color?: RawColor } | undefined>
      const slave = (r: number, c: number) => ws.findRow(r)?.findCell(c)?.border as Record<string, { style?: string; color?: RawColor } | undefined> | undefined
      const rawTop = edgeOf(border.top)
      const rawLeft = edgeOf(border.left)
      // 병합 영역의 오른쪽·아래 테두리는 그 끝 칸에 적혀 있을 수 있다 — 없으면 왼쪽 위 셀의 것을 쓴다
      const rawRight = edgeOf(merge ? (slave(rn, merge.c2)?.right ?? border.right) : border.right)
      const rawBottom = edgeOf(merge ? (slave(merge.r2, cn)?.bottom ?? border.bottom) : border.bottom)
      if (!text && !fill && !rawTop && !rawLeft && !rawRight && !rawBottom && !merge) continue

      const font = cell.font ?? {}
      const al = cell.alignment ?? {}
      const horizontal = al.horizontal as string | undefined
      const align: SheetBox['align'] =
        horizontal === 'center' || horizontal === 'centerContinuous' || horizontal === 'distributed' ? 'center'
          : horizontal === 'right' ? 'right'
            : horizontal === 'left' || horizontal === 'fill' || horizontal === 'justify' ? 'left'
              : kind === 'number' ? 'right' : kind === 'bool' || kind === 'error' ? 'center' : 'left'
      const v = al.vertical as string | undefined
      const fontSize = Math.round((font.size ?? 11) * 96 / 72 * 10) / 10
      kinds.push(kind)
      drafts.push({
        key: key(rn, cn),
        row: rn - r0,
        col: cn - c0,
        rowSpan: merge ? merge.r2 - merge.r1 + 1 : 1,
        colSpan: merge ? merge.c2 - merge.c1 + 1 : 1,
        // 줄바꿈이 꺼진 칸은 엑셀이 한 줄로 보여 준다
        text: al.wrapText ? text : text.replace(/[\r\n]+/g, ' '),
        fill,
        color: resolveColor(font.color as RawColor | undefined) ?? SHEET_INK,
        font: font.name ? `"${font.name}", sans-serif` : undefined,
        fontSize,
        bold: !!font.bold,
        italic: !!font.italic,
        underline: !!font.underline,
        strike: !!font.strike,
        align,
        valign: v === 'top' ? 'flex-start' : v === 'middle' || v === 'center' ? 'center' : 'flex-end',
        wrap: !!al.wrapText,
        indent: al.indent ?? 0,
        spill: false,
        rawTop, rawLeft, rawRight, rawBottom,
      })
    }
  }

  // 어느 칸을 어느 상자가 덮는지
  const owner = new Map<string, number>()
  const cover = (i: number) => {
    const d = drafts[i]
    for (let r = 0; r < d.rowSpan; r++) for (let c = 0; c < d.colSpan; c++) owner.set(key(d.row + r, d.col + c), i)
  }
  drafts.forEach((_, i) => cover(i))

  // 글자 넘침 — 엑셀은 왼쪽 정렬 글자가 옆이 빈 칸이면 그 위로 이어서 쓴다(채우기·테두리·줄바꿈이 없을 때만 흉내 낸다)
  drafts.forEach((d, i) => {
    if (kinds[i] !== 'text' || !d.text || d.wrap || d.align !== 'left' || d.fill || d.rowSpan > 1 || d.colSpan > 1) return
    if (d.rawTop || d.rawLeft || d.rawRight || d.rawBottom) return
    const need = approxTextWidth(d.text.split('\n')[0], d.fontSize) + 8
    let have = colWidths[d.col]
    while (have < need && d.col + d.colSpan < nCols && !owner.has(key(d.row, d.col + d.colSpan)) && colWidths[d.col + d.colSpan] > 0) {
      owner.set(key(d.row, d.col + d.colSpan), i)
      have += colWidths[d.col + d.colSpan]
      d.colSpan++
    }
    d.spill = d.colSpan > 1
  })

  // 테두리 — 이웃한 두 칸이 같은 선을 둘 다 가졌으면 한 번만 그린다(겹쳐 두꺼워지지 않게).
  // 위·왼쪽은 "내 것, 없으면 위·왼쪽 이웃의 아래·오른쪽", 아래·오른쪽은 이웃 상자가 없을 때만 그린다.
  const at = (r: number, c: number): Draft | undefined => {
    const i = owner.get(key(r, c))
    return i == null ? undefined : drafts[i]
  }
  const boxes: SheetBox[] = drafts.map(d => {
    const { rawTop, rawLeft, rawRight, rawBottom, ...box } = d
    return {
      ...box,
      top: rawTop ?? at(d.row - 1, d.col)?.rawBottom,
      left: rawLeft ?? at(d.row, d.col - 1)?.rawRight,
      right: at(d.row, d.col + d.colSpan) ? undefined : rawRight,
      bottom: at(d.row + d.rowSpan, d.col) ? undefined : rawBottom,
    }
  })

  // 이미지(도장·로고) — 시작 칸의 위치(소수 포함)를 px 로 바꾼다
  const colX = [0]
  colWidths.forEach(w => colX.push(colX[colX.length - 1] + w))
  const rowY = [0]
  rowHeights.forEach(h => rowY.push(rowY[rowY.length - 1] + h))
  // 그림 위치는 0 부터 세는 소수 칸 번호(0.5 = 첫 칸의 절반)다 — 창이 시작하는 칸(c0·r0, 1 부터)만큼 빼서 px 로 바꾼다
  const posX = (x: number) => {
    const i = Math.floor(x) - (c0 - 1)
    return i < 0 ? 0 : i >= nCols ? colX[nCols] : colX[i] + (x - Math.floor(x)) * colWidths[i]
  }
  const posY = (y: number) => {
    const i = Math.floor(y) - (r0 - 1)
    return i < 0 ? 0 : i >= nRows ? rowY[nRows] : rowY[i] + (y - Math.floor(y)) * rowHeights[i]
  }
  const images: SheetImage[] = []
  for (const im of ws.getImages()) {
    try {
      const media = wb.getImage(Number(im.imageId))
      const rg = im.range as { tl: { col: number; row: number }; br?: { col: number; row: number }; ext?: { width: number; height: number } }
      if (!media?.buffer || rg.tl.col < c0 - 1 || rg.tl.row < r0 - 1 || rg.tl.col >= cEnd || rg.tl.row >= rEnd) continue
      const left = posX(rg.tl.col), top = posY(rg.tl.row)
      const width = rg.ext ? rg.ext.width : rg.br ? posX(rg.br.col) - left : 0
      const height = rg.ext ? rg.ext.height : rg.br ? posY(rg.br.row) - top : 0
      if (width <= 0 || height <= 0) continue
      const ext = (media.extension ?? 'png').toLowerCase()
      images.push({ data: media.buffer as unknown as Uint8Array, mime: ext === 'jpg' || ext === 'jpeg' ? 'image/jpeg' : `image/${ext}`, left, top, width, height })
    } catch { /* 읽을 수 없는 그림은 건너뛴다 */ }
  }

  // 그림이 마지막 칸을 넘어가면 종이도 그만큼 넓힌다
  const width = Math.max(colX[colX.length - 1], ...images.map(i => i.left + i.width))
  const height = Math.max(rowY[rowY.length - 1], ...images.map(i => i.top + i.height))
  return { name: ws.name, colWidths, rowHeights, boxes, images, width, height, clipped }
}
